import RealtimeKitCallClient from 'dashboard/api/channel/voice/realtimeKitClient';
import AgoraCallClient from 'dashboard/api/channel/voice/agoraCallClient';
import { WEB_CALL_ENGINES } from 'shared/composables/useWebCallAudio';
import { useWebCallRecorder } from 'dashboard/composables/useWebCallRecorder';

const RELAYED_EVENTS = ['call:disconnected', 'call:peer-left'];
const recorder = useWebCallRecorder();

/**
 * Routes dashboard web-call audio to whichever engine the call was created on.
 *
 * The engine arrives on the `connection` payload from the create/accept
 * endpoints (see Voice::Web::TokenService) and is fixed for the life of a call.
 * Both underlying clients emit the same event names, and this re-emits them
 * from the one that is live — so useCallSession attaches its global listeners
 * once, to this, regardless of engine.
 *
 * It also records the call when that payload says the account records calls
 * (`connection.recording`): the engine reports each side's audio as it
 * arrives, and every way the call can end for this agent (hang-up, the
 * customer leaving, the connection dropping) uploads the recording once.
 */
class WebCallClient extends EventTarget {
  constructor() {
    super();
    this.active = null;
    // The Call#id the recording uploads to; null when not recording.
    this.recordingCallId = null;
    [RealtimeKitCallClient, AgoraCallClient].forEach(client => {
      RELAYED_EVENTS.forEach(name => {
        client.addEventListener(name, () => {
          // A superseded client tearing itself down must not end the call that
          // replaced it.
          if (this.active !== client) return;
          this.finishRecording();
          this.dispatchEvent(new CustomEvent(name));
        });
      });
      client.addEventListener('call:audio-tracks', () => {
        if (this.active !== client || !this.recordingCallId) return;
        try {
          recorder.start(client.getAudioTracks());
        } catch (_) {
          // A single failing audio-tracks update must not break the call the
          // recording rides on.
        }
      });
    });
  }

  async connect(connection, callId) {
    // A recording left from an earlier call must never take this call's audio.
    this.finishRecording();
    this.recordingCallId = connection?.recording ? callId : null;
    if (connection?.engine === WEB_CALL_ENGINES.AGORA) {
      this.active = AgoraCallClient;
      await AgoraCallClient.connect(connection);
      return;
    }
    this.active = RealtimeKitCallClient;
    // RealtimeKit encodes the room and identity inside the token, so it takes
    // the bare string; Agora needs the channel and uid alongside it.
    await RealtimeKitCallClient.connect(connection.token);
  }

  // The recording is finished first, while both tracks are still live.
  // `active` is cleared only after the underlying leave resolves, so
  // RealtimeKit's roomLeft — which fires during it — still relays through.
  async disconnect() {
    this.finishRecording();
    if (!this.active) return;
    await this.active.disconnect();
    this.active = null;
  }

  finishRecording() {
    if (!this.recordingCallId) return;
    // Not awaited: a long call's upload must not hold up hanging up.
    recorder.stopAndUpload(this.recordingCallId);
    this.recordingCallId = null;
  }

  // For a join that never completed (e.g. a transfer whose completion call
  // failed after the room was already live): drop whatever was captured
  // instead of uploading it. The server's upload is idempotent — the first
  // one wins — so a partial clip uploaded here would permanently shadow the
  // real recording of the call that actually continues.
  discardRecording() {
    this.recordingCallId = null;
    recorder.discard();
  }

  async setMuted(muted) {
    await this.active?.setMuted(muted);
  }

  async setHold(held) {
    await this.active?.setHold(held);
    if (held) {
      recorder.pause();
    } else {
      recorder.resume();
    }
  }

  get isConnected() {
    return Boolean(this.active?.isConnected);
  }
}

export default new WebCallClient();
